// 미디어 저장(다운로드) 중앙 어댑터.
//
// 계약 전제: 저장용 URL 은 CHAT 의 `?disposition=attachment` SAS 다. Azure 가
// Content-Disposition: attachment 와 Content-Type 을 서명에 포함하므로, cross-origin 이라도
// 브라우저가 `<a download>` 클릭을 실제 다운로드로 처리한다. 따라서 새 탭(window.open) 경로는 없다 —
// 새 탭은 저장이 아니라 "보기"였고, 팝업 차단·앱 내 브라우저에서 막다른 길이었다.
//
// 분기 순서:
//   1. native saveMedia 브리지가 있으면 위임하고 **결과 콜백을 기다린다** (아래 계약)
//   2. 없으면 숨겨진 <a download> 를 붙였다 클릭하고 즉시 제거 (same/cross-origin 동일)
//
// 저장 경로를 이 한 곳에 모아, 브리지 계약이 바뀌면 여기만 고치면 되게 한다.
//
// native saveMedia 계약 (AOS `WebAppInterface.saveMedia` / iOS `WebViewBridge` "saveMedia" 액션):
//   요청
//     AOS: window.Android.saveMedia(JSON.stringify({ requestId, attachmentKey, url, filename, mimeType }))
//     iOS: window.webkit.messageHandlers.nativeApp.postMessage({ action: "saveMedia", ...같은 필드 })
//          iOS 의 capability 는 document start 에 주입되는 window.__BUCCL_NATIVE_CAPABILITIES__.saveMedia 다.
//          messageHandlers.nativeApp 이 있다는 것만으로는 saveMedia 를 아는 앱이라고 볼 수 없다
//          (구버전은 "알 수 없는 액션" 으로 버리고 콜백을 주지 않는다).
//   결과
//     window.__bucclNativeSaveMediaResult({ requestId, attachmentKey, success, code, destination })
//     code: SUCCESS | CANCELLED | PERMISSION_DENIED | INVALID_REQUEST | UNSUPPORTED_URL |
//           URL_EXPIRED | NETWORK_ERROR | STORAGE_FULL | SAVE_FAILED
//   규칙
//     - 호출이 반환된 것은 저장 완료가 아니다. 결과는 콜백으로만 온다.
//     - 콜백은 requestId 당 최대 한 번이지만 **미도달은 가능하다** (WebView 파괴·도메인 이탈·
//       세션 교체). 그래서 FE timeout 이 마지막 방어선이다.
//     - 식별자(requestId/attachmentKey)는 iOS 기준 ASCII [A-Za-z0-9_-] 1~128자. 벗어나면 iOS 는
//       콜백 없이 조용히 버린다. attachmentKey 는 호출부가 준 값을 **변형하지 않고** 검증만 한다 —
//       바꿔서 보내면 콜백의 attachmentKey 가 호출부의 신원과 어긋난다.
//     - 네이티브에 위임한 뒤에는(실패·취소·결과 미확인·호출 throw 포함) anchor 로 되돌아가지 않는다.
//       네이티브가 이미 저장했거나 사용자가 취소한 것을 웹이 다시 시도하면 중복 저장이 된다.
//     - FE 의 대기 시간이 길어졌다는 것만으로 실패라고 말하지 않는다. 네이티브는 다운로드(iOS 리소스
//       상한 600s, AOS 는 상한 없음)와 저장 위치 선택(사용자 대기)에 그만큼 걸릴 수 있다. 그래서
//       대기 중 통지는 두 단계다 — NATIVE_SAVE_PENDING_NOTICE_MS 에 "아직 저장 중", NATIVE_SAVE_UNKNOWN_AFTER_MS 에
//       "아직 결과를 받지 못했다". **둘 다 종결이 아니다.** Promise 는 네이티브의 실제 종결 결과(콜백)에서만
//       settle 되고, requestId+attachmentKey 상관관계와 같은 첨부의 중복 요청 차단도 그때까지 유지된다.
//       FE 타이머로 종결해 버리면 늦게 온 진짜 결과를 버리고 같은 첨부를 한 번 더 저장하게 된다(리뷰 재현).
//     - 결과를 모르는 동안 같은 첨부의 저장 요청은 진행 중 요청에 **합류**한다(새 네이티브 요청 없음).
//       늦게 성공할 저장을 사용자가 한 번 더 눌러 두 번 저장하는 것이 fallback 금지의 근거와 같은 문제다.
//     - 생명주기 경계 = **문서(document)**. 네이티브도 같은 경계에서 콜백을 포기한다(AOS: WebView destroy 시
//       cancelAll, iOS: 문서/세션 교체 시 세대 bump → 콜백 차단). 그래서 `pagehide` 에 진행 중 요청을 모두
//       UNKNOWN(결과 미확인)으로 settle 하고 장부를 비운다 — 콜백이 올 수 없는 요청을 붙들지 않기 위해서다.
//       그 밖의 시간 기준 리셋은 없다(네이티브에 취소/리셋 계약이 없으므로 FE 가 지어내지 않는다).
//     - 메모리: 장부 항목은 "실제 결과를 못 받은 첨부" 수만큼만 남는다(종결·pagehide 에서 제거). 통지 리스너는
//       단계가 발화되면 비워지고, 이미 지난 단계에 합류한 호출은 즉시 통지받고 저장되지 않는다 — 합류자가
//       늘어도 리스너가 무한히 쌓이지 않는다. Promise 대기자는 Promise 자체가 관리한다.

const RESULT_CALLBACK_NAME = "__bucclNativeSaveMediaResult";
const NATIVE_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
// 이 시점부터 호출부는 "저장하는 중이에요" 를 보여 준다. 영상 최대 200MB 기준 통상 완료 시간.
export const NATIVE_SAVE_PENDING_NOTICE_MS = 120_000;
// 이 시점까지 콜백이 없으면 "아직 결과를 받지 못했다" 고 알린다(종결 아님). iOS 다운로드 리소스 상한(600s)보다 길게 둔다.
export const NATIVE_SAVE_UNKNOWN_AFTER_MS = 15 * 60_000;
export const NATIVE_SAVE_PENDING_MESSAGE = "저장하는 중이에요. 시간이 걸릴 수 있어요.";
export const NATIVE_SAVE_UNKNOWN_MESSAGE =
    "아직 저장 결과를 받지 못했어요. 앱이 저장을 마치면 알려드릴게요. 사진첩이나 파일 앱에서도 확인할 수 있어요.";

// requestId → { attachmentKey, settle, subscribe, promise }. 콜백(종결)·pagehide 에서만 항목을 지우므로
// 늦은 콜백은 종결 전이면 반영되고, 종결 후·중복 콜백은 여기서 걸러진다.
const pendingRequests = new Map();
// attachmentKey → requestId. 결과를 모르는 동안 같은 첨부의 요청을 합류시키는 색인.
const inFlightByAttachmentKey = new Map();
let requestSeq = 0;
let lifecycleBoundaryInstalled = false;

// 문서 경계: 콜백이 도달할 수 없게 되는 시점. 진행 중 요청을 UNKNOWN 으로 settle 하고 장부를 비운다.
const installLifecycleBoundary = () => {
    if (lifecycleBoundaryInstalled) return;
    lifecycleBoundaryInstalled = true;
    window.addEventListener("pagehide", () => {
        [...pendingRequests.values()].forEach((pending) => pending.settle({ via: "native", success: false, code: "UNKNOWN" }));
    });
};

const nextRequestId = () => `sm-${Date.now()}-${(requestSeq += 1)}`;

const parseResultPayload = (payload) => {
    if (typeof payload === "string") {
        try {
            return JSON.parse(payload);
        } catch {
            return null;
        }
    }
    return payload && typeof payload === "object" ? payload : null;
};

// 전역 단일 진입점. 두 플랫폼이 같은 이름 하나를 부른다.
const handleNativeSaveMediaResult = (rawPayload) => {
    const payload = parseResultPayload(rawPayload);
    if (!payload) return;
    const pending = pendingRequests.get(payload.requestId);
    if (!pending) return;
    // requestId 와 attachmentKey 가 **둘 다** 정확히 일치해야 한다. attachmentKey 가 빠진 콜백은
    // 일치가 아니다 — 같은 requestId 로 다른 첨부의 결과를 받아들이면 안 된다.
    if (payload.attachmentKey !== pending.attachmentKey) return;
    const result = {
        via: "native",
        success: payload.success === true,
        code: typeof payload.code === "string" ? payload.code : "SAVE_FAILED",
    };
    if (payload.destination !== undefined) result.destination = payload.destination;
    pending.settle(result);
};

const installResultCallback = () => {
    if (window[RESULT_CALLBACK_NAME] !== handleNativeSaveMediaResult) {
        window[RESULT_CALLBACK_NAME] = handleNativeSaveMediaResult;
    }
};

// 브리지별 전송 함수를 돌려준다. 없으면 null → 웹 fallback.
const getNativeSender = () => {
    if (typeof window === "undefined") return null;
    const android = window.Android;
    if (android && typeof android.saveMedia === "function") {
        return (request) => android.saveMedia(JSON.stringify(request));
    }
    const handler = window.webkit?.messageHandlers?.nativeApp;
    if (
        window.__BUCCL_NATIVE_CAPABILITIES__?.saveMedia === true &&
        handler &&
        typeof handler.postMessage === "function"
    ) {
        return (request) => handler.postMessage({ action: "saveMedia", ...request });
    }
    return null;
};

const saveViaNative = (send, url, filename, { attachmentKey, mimeType, onPending, onUnknown }) => {
    if (typeof attachmentKey !== "string" || !NATIVE_ID_PATTERN.test(attachmentKey)) {
        return Promise.resolve({ via: "native", success: false, code: "INVALID_ATTACHMENT_KEY" });
    }

    // 같은 첨부의 결과를 아직 모른다면 새 요청을 보내지 않고 그 결과를 함께 기다린다.
    const inFlight = pendingRequests.get(inFlightByAttachmentKey.get(attachmentKey));
    if (inFlight) {
        inFlight.subscribe({ onPending, onUnknown });
        return inFlight.promise;
    }

    const requestId = nextRequestId();
    // 단계별 통지. 발화 전에는 리스너를 모아 두고, 발화 뒤에 합류한 호출은 즉시 통지받는다(저장하지 않는다).
    const stages = {
        pending: { fired: false, listeners: [], delay: NATIVE_SAVE_PENDING_NOTICE_MS, timer: null },
        unknown: { fired: false, listeners: [], delay: NATIVE_SAVE_UNKNOWN_AFTER_MS, timer: null },
    };
    let resolvePromise;
    const promise = new Promise((resolve) => {
        resolvePromise = resolve;
    });

    const settle = (result) => {
        if (!pendingRequests.has(requestId)) return;
        pendingRequests.delete(requestId);
        if (inFlightByAttachmentKey.get(attachmentKey) === requestId) inFlightByAttachmentKey.delete(attachmentKey);
        Object.values(stages).forEach((stage) => {
            clearTimeout(stage.timer);
            stage.listeners.length = 0;
        });
        resolvePromise(result);
    };
    const subscribe = ({ onPending: pendingListener, onUnknown: unknownListener }) => {
        [
            [stages.pending, pendingListener],
            [stages.unknown, unknownListener],
        ].forEach(([stage, listener]) => {
            if (typeof listener !== "function") return;
            if (stage.fired) listener();
            else stage.listeners.push(listener);
        });
    };

    installResultCallback();
    installLifecycleBoundary();
    pendingRequests.set(requestId, { attachmentKey, settle, subscribe, promise });
    inFlightByAttachmentKey.set(attachmentKey, requestId);
    subscribe({ onPending, onUnknown });
    Object.values(stages).forEach((stage) => {
        stage.timer = setTimeout(() => {
            stage.fired = true;
            stage.listeners.splice(0).forEach((listener) => listener());
        }, stage.delay);
    });

    try {
        send({
            requestId,
            attachmentKey,
            url,
            filename: typeof filename === "string" ? filename : "",
            mimeType: typeof mimeType === "string" ? mimeType : "",
        });
    } catch {
        // 브리지 호출 자체가 실패했다. 네이티브 갈래를 탄 이상 anchor 로 되돌리지 않는다.
        settle({ via: "native", success: false, code: "BRIDGE_ERROR" });
    }
    return promise;
};

// 결과 코드 → 사용자 안내 분류. 호출부(파일 행·뷰어)가 같은 문구를 쓰도록 한 곳에 둔다.
//   permanent: 재시도만으로는 결과가 같다 (권한·정책·요청 형식·공간 부족 — 사용자/설정 쪽 조치 필요)
//   transient: 다시 시도해 볼 만하다 (네트워크·SAS 만료·일시 실패·브리지 오류·모르는 코드)
//   unknown:   문서 경계(pagehide)에서 결과 없이 정리된 요청. 실패로 말하지 않고 확인을 권한다.
const PERMANENT_MESSAGES = {
    // AOS 채팅 저장 경로(MediaStore API29+ / SAF API24–28)에는 "앱 설정에서 권한 허용" 같은 조치가 없다.
    // 존재하지 않는 조치나 재시도 효과를 약속하지 않는 중립 사실 문구만 쓴다(iOS 도 같은 문구).
    PERMISSION_DENIED: "이 저장 위치에 접근할 수 없어 저장하지 못했어요.",
    STORAGE_FULL: "저장 공간이 부족해요. 공간을 확보한 뒤 다시 시도해 주세요.",
    UNSUPPORTED_URL: "이 첨부는 앱에서 저장할 수 없어요.",
    INVALID_REQUEST: "이 첨부는 앱에서 저장할 수 없어요.",
    INVALID_ATTACHMENT_KEY: "이 첨부는 앱에서 저장할 수 없어요.",
};

export const describeNativeSaveResult = (result) => {
    if (!result || result.via !== "native") return null;
    if (result.success) return { kind: "success", message: "저장했어요" };
    if (result.code === "CANCELLED") return { kind: "cancelled", message: "" };
    if (result.code === "UNKNOWN") {
        return { kind: "unknown", message: "저장 결과를 확인하지 못했어요. 사진첩이나 파일 앱에서 확인해 주세요." };
    }
    if (PERMANENT_MESSAGES[result.code]) return { kind: "permanent", message: PERMANENT_MESSAGES[result.code] };
    return { kind: "transient", message: "저장하지 못했어요. 잠시 후 다시 시도해 주세요." };
};

// anchor.href 에 넣고 프로그래매틱 click() 하는 구조라, scheme 을 거르지 않으면
// `javascript:` URL 이 그대로 실행된다. 저장에 필요한 것은 http(s) 뿐이므로
// 나머지(javascript/data/blob/scheme 없는 상대 경로)는 아예 클릭하지 않는다.
// native saveMedia 위임은 이 검사와 무관하게 기존 순서대로 우선한다.
const isDownloadableUrl = (url) => {
    try {
        // base 를 주지 않는다. 저장 URL 은 항상 절대 http(s) SAS 이고,
        // scheme 없는 값(`//host/...`, `/path`)까지 http 로 승격시킬 이유가 없다.
        const { protocol } = new URL(url);
        return protocol === "http:" || protocol === "https:";
    } catch {
        return false;
    }
};

const saveViaAnchor = (url, filename) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename || "";
    anchor.rel = "noopener";
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    try {
        anchor.click();
    } finally {
        document.body.removeChild(anchor);
    }
    return { via: "anchor" };
};

// meta = { attachmentKey, mimeType, onPending?, onUnknown? } — 네이티브 브리지에만 쓰인다. 웹 fallback 은 url/filename 만 본다.
// onPending / onUnknown 은 각 임계치가 지나도 결과가 없을 때 한 번씩 불린다(안내 전환 시점). 종결이 아니다.
// 반환(네이티브의 실제 종결 결과 또는 문서 경계에서만 settle):
//   { via: "native", success, code, destination? }  네이티브 결과 (실패·취소·BRIDGE_ERROR, pagehide 시 UNKNOWN 포함)
//   { via: "anchor" }                                 웹 fallback 시작 (완료 여부는 알 수 없음)
//   { via: "none" }                                   저장 수단 없음
export const downloadMedia = async (url, filename, meta = {}) => {
    if (!url || typeof window === "undefined") return { via: "none" };

    const send = getNativeSender();
    if (send) return saveViaNative(send, url, filename, meta || {});

    if (!isDownloadableUrl(url)) return { via: "none" };

    try {
        return saveViaAnchor(url, filename);
    } catch {
        // anchor 조차 실패하면 저장 수단이 없다. 새 탭으로 눈속임하지 않고 실패를 알린다.
        return { via: "none" };
    }
};

export default downloadMedia;
